/** Basic fields prevalent in all API results */
export type BaseAPIResult = {
  /** URL of the request */
  url: string;

  /** Whether or not the call succeeded */
  success: boolean;

  /** HTTP status of the response */
  status: number;

  /** Content-Type header of the response */
  contentType?: string;

  /** Content-Disposition header of the response */
  contentDisposition?: string;
};

/** Successful API result */
export type SuccessfulAPIResult<T = unknown> = BaseAPIResult & {
  success: true;

  /** Specific data of the response */
  data: T;
};

/** Errored API result */
export type ErroredAPIResult<E = unknown> = BaseAPIResult & {
  success: false;

  /** Specific data of the response */
  errorData: E;
};

/** Overarching API result type */
export type APIResult<T = unknown, E = unknown> =
  | SuccessfulAPIResult<T>
  | ErroredAPIResult<E>;

export type Headers = { [key: string]: string };
export type Method = 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH';

/**
 * Interface representing options that can be given for the underlying API
 * commands in this package
 */
export interface FetchOpts {
  /** Method to issue with the request */
  method?: Method;

  /** Headers to set explicitly */
  headers?: Headers;
}

/** Options for creating a new Api const */
export type APIOptions = {
  protocol?: string;
  hostname?: string;
  port?: string;
  basePath?: string;

  /**
   * Skip the credentials: true option when using fetch
   */
  noCredentials?: boolean;

  /**
   * Inject your own custom normalizeURL function
   */
  normalizeURL?: (url: string) => string;

  /**
   * Inject additional headers into all API calls based on the given method and
   * url
   */
  additionalHeaders?: (method: Method, url: string) => Headers;

  /** Inject additional headers for authenticated APIs */
  authHeaders?: (method: Method, url: string) => Headers;

  /**
   * Hook to be called after an authed API result has been received. This can
   * be used to inject an effect on a response. For example, delete
   * authentication credentials on a 401.
   */
  postAuthHook?: (result: APIResult) => void;
};

export type Body = Record<string, unknown>;

export type FetchAPIType<T = unknown, E = unknown> = (
  url: string,
  opts?: FetchOpts,
  body?: Body | FormData
) => Promise<APIResult<T, E>>;

export type SimpleFetchAPIType<T = unknown, E = unknown> = (
  url: string,
  body?: Body
) => Promise<APIResult<T, E>>;

export type UploadAPIType<T = unknown, E = unknown> = (
  url: string,
  key: string,
  file: File
) => Promise<APIResult<T, E>>;

/** Type represented an API created for a specific client configuration */
export type API<T = unknown, E = unknown> = {
  fetchAPI: FetchAPIType<T, E>;
  fetchAPIRaw: (
    url: string,
    opts?: FetchOpts,
    body?: BodyInit
  ) => Promise<APIResult<T, E>>;
  fetchAPIUnauthed: FetchAPIType<T, E>;

  get: SimpleFetchAPIType<T, E>;
  getUnauthed: SimpleFetchAPIType<T, E>;
  post: SimpleFetchAPIType<T, E>;
  postUnauthed: SimpleFetchAPIType<T, E>;
  put: SimpleFetchAPIType<T, E>;
  putUnauthed: SimpleFetchAPIType<T, E>;
  del: SimpleFetchAPIType<T, E>;
  delUnauthed: SimpleFetchAPIType<T, E>;
  upload: UploadAPIType<T, E>;
  downloadResult: (result: APIResult<Blob, E>) => void;
};

export function createAPI<T, E>(options: APIOptions = {}): API<T, E> {
  /**
   * Normalize the given URL with respect to the backend API server. In this
   * way, the package can take absolute URLs, paths, and URLs with invalid
   * schemas.
   *
   * @param url - url to normalize
   */
  const normalizeURL = (url: string): string => {
    const protocol = options.protocol ?? window.location.protocol;
    const hostname = options.hostname ?? window.location.hostname;
    const port = options.port ?? window.location.port;
    const host = port ? `${hostname}:${port}` : hostname;
    const fullHost = `${protocol}//${host}`;
    const fullHostMinusPort = `${protocol}//${hostname}`;

    if (url.startsWith(fullHost)) {
      // make absolute urls relative if needed
      url = url.substr(fullHost.length);
    } else if (url.startsWith(fullHostMinusPort)) {
      // Sometimes, we get "next_page" URLs that don't include the port. That
      // seems to be a flaw in our pagination layer. Just do the same thing if
      // port isn't given.
      url = url.substr(fullHostMinusPort.length);
    }

    const basePath = options.basePath ?? '/';
    if (basePath && url.startsWith(basePath)) {
      // If the url starts with our base path (/api/), then assume it's already
      // good to use.
      return url;
    } else if (url.startsWith('/')) {
      // If it starts with a leading '/', we have to strip that off before
      // applying our base path.
      return basePath + url.slice(1);
    } else {
      // If not, just append to the end of our base path. /api/<url>
      return basePath + url;
    }
  };

  /**
   * Issue a raw fetch operation
   *
   * @param url - url to request
   * @param opts - options to use
   * @param body - body of the request
   */
  const fetchAPIRaw = function (
    url: string,
    opts?: FetchOpts,
    body?: BodyInit
  ): Promise<APIResult<T, E>> {
    url = options.normalizeURL ? options.normalizeURL(url) : normalizeURL(url);
    const result = {
      url,
    };
    const method = opts?.method ?? 'GET';
    const headers = opts?.headers ?? {};
    const newHeaders: Headers = options.additionalHeaders
      ? options.additionalHeaders(method, url)
      : {};
    let success = false;
    let status: number;
    let contentType: string;
    let contentDisposition: string;

    const fetchOpts: any = {
      method,
      headers: {
        ...newHeaders,
        ...headers,
      },
      body,
    };
    if (!options.noCredentials) {
      fetchOpts.credentials = 'include';
    }

    return fetch(url, fetchOpts)
      .then((response: Response) => {
        status = response.status;

        // This value seems unpredictable from node-fetch. As such, just go
        // ahead and use their same mechanism to figure it out for ourselves.
        success = status >= 200 && status < 300;

        // No content
        if (status === 204) {
          return null;
        }

        const gen = response.headers.entries();
        for (let entry = gen.next(); !entry.done; entry = gen.next()) {
          const [headerName, headerValue] = entry.value;
          if (headerName === 'content-type') {
            contentType = headerValue;
            continue;
          }
          if (headerName === 'content-disposition') {
            contentDisposition = headerValue;
            continue;
          }
        }

        if (contentType === 'application/json') {
          return response.json();
        }

        // Deal with the blob however you like.
        return response.blob();
      })
      .then((data) => {
        if (success) {
          return {
            ...result,
            success: true,
            status,
            contentType,
            contentDisposition,
            data,
          };
        }

        return {
          ...result,
          success: false,
          status,
          contentType,
          contentDisposition,
          errorData: data,
        };
      });
  };

  /**
   * Issue an unauthenticated fetch operation
   *
   * @param url - url to fetch
   * @param opts - options to use
   * @param body - body of the request
   */
  const fetchAPIUnauthed = function (
    url: string,
    opts?: FetchOpts,
    body?: Body | FormData
  ): Promise<APIResult<T, E>> {
    const headers = opts?.headers ?? {};
    const newHeaders: Headers = {};
    let requestBody;
    if (body) {
      if (body instanceof FormData) {
        requestBody = body;
        delete headers['Content-Type'];
      } else {
        newHeaders['Content-Type'] = 'application/json';
        requestBody = JSON.stringify(body);
      }
    }
    return fetchAPIRaw(
      url,
      {
        ...opts,
        headers: {
          ...newHeaders,
          ...headers,
        },
      },
      requestBody
    );
  };

  /**
   * Issue an authenticated fetch operation
   *
   * @param url - url to fetch
   * @param opts - options to use
   * @param body - body of the request
   */
  const fetchAPI = function (
    url: string,
    opts?: FetchOpts,
    body?: Body | FormData
  ): Promise<APIResult<T, E>> {
    const headers = opts?.headers ?? {};
    const method = opts?.method ?? 'GET';
    const newHeaders: Headers = options.authHeaders
      ? options.authHeaders(method, url)
      : {};

    return fetchAPIUnauthed(
      url,
      {
        method,
        headers: {
          ...newHeaders,
          ...headers,
        },
      },
      body
    ).then((result) => {
      if (options.postAuthHook) {
        options.postAuthHook(result);
      }
      return result;
    });
  };

  /**
   * Issue a get
   *
   * @param url - url to fetch
   * @param body - body to send in the request
   */
  const get = function (url: string, body?: Body): Promise<APIResult<T, E>> {
    return fetchAPI(url, { method: 'GET' }, body);
  };

  /**
   * Issue an unauthenticated get
   *
   * @param url - url to fetch
   * @param body - body to send in the request
   */
  const getUnauthed = function (
    url: string,
    body?: Body
  ): Promise<APIResult<T, E>> {
    return fetchAPIUnauthed(url, { method: 'GET' });
  };

  /**
   * Issue a post
   *
   * @param url - url to fetch
   * @param body - body to send in the request
   */
  const post = function (url: string, body?: Body): Promise<APIResult<T, E>> {
    return fetchAPI(url, { method: 'POST' }, body);
  };

  /**
   * Issue an unauthenticated post
   *
   * @param url - url to fetch
   * @param body - body to send in the request
   */
  const postUnauthed = function (
    url: string,
    body?: Body
  ): Promise<APIResult<T, E>> {
    return fetchAPIUnauthed(url, { method: 'POST' }, body);
  };

  /**
   * Issue a put
   *
   * @param url - url to fetch
   * @param body - body to send in the request
   */
  const put = function (url: string, body?: Body): Promise<APIResult<T, E>> {
    return fetchAPI(url, { method: 'PUT' }, body);
  };

  /**
   * Issue an unauthed put
   *
   * @param url - url to fetch
   * @param body - body to send in the request
   */
  const putUnauthed = function (
    url: string,
    body?: Body
  ): Promise<APIResult<T, E>> {
    return fetchAPIUnauthed(url, { method: 'PUT' }, body);
  };

  /**
   * Issue a delete
   *
   * @param url - url to fetch
   * @param body - body to send in the request
   */
  const del = function (url: string, body?: Body): Promise<APIResult<T, E>> {
    return fetchAPI(url, { method: 'DELETE' }, body);
  };

  /**
   * Issue an unauthed delete
   *
   * @param url - url to fetch
   * @param body - body to send in the request
   */
  const delUnauthed = function (
    url: string,
    body?: Body
  ): Promise<APIResult<T, E>> {
    return fetchAPIUnauthed(url, { method: 'DELETE' }, body);
  };

  /**
   * Issue a file upload
   *
   * @param url
   * @param key
   * @param file
   */
  const upload = function (
    url: string,
    key: string,
    file: File
  ): Promise<APIResult<T, E>> {
    const formData = new FormData();
    formData.set(key, file);
    return fetchAPI(url, { method: 'POST' }, formData);
  };

  /**
   * Download a result as a file
   *
   * @param result - result to download
   */
  const downloadResult = function (result: APIResult<Blob, E>): void {
    if (!result.success) {
      return;
    }

    // This looks funky, but this is the "accepted" way to download a file in
    // JavaScript. The given result should have a blob for data, now use it to
    // create a "link" and "click it".
    const downloadURL = window.URL.createObjectURL(result.data);
    const a = document.createElement('a');
    a.href = downloadURL;

    // Now, we need to find the filename and set the "download" value of the link

    // We need this value to figure out the filename. If we don't have it, just
    // return and do nothing.
    if (!result.contentDisposition) {
      return;
    }

    // Like 'inline; filename="filename.pdf"'
    const dispositionToks = result.contentDisposition.split(' ');
    for (const i in dispositionToks) {
      const tok = dispositionToks[i];
      if (tok.startsWith('filename=')) {
        a.download = tok.substring(9).replace(/"/, '');
        break;
      }
    }
    a.click();
  };

  return {
    fetchAPI,
    fetchAPIRaw,
    fetchAPIUnauthed,

    get,
    getUnauthed,
    post,
    postUnauthed,
    put,
    putUnauthed,
    del,
    delUnauthed,
    upload,
    downloadResult,
  };
}
